import {
  deserializePermissionsCookie,
  PERMISSIONS_COOKIE_NAME,
} from "@/lib/permissions-cookie";
import { PermissionDeniedError } from "@/lib/errors";
import { IPathPermission } from "@/types/base-type";
import { RBACConfig } from "@/features/privileges/roles/types";
import Cookies from "js-cookie";
import { toast } from "sonner";

export type ServerActionResult<T> =
  | { success: true; value: T }
  | { success: false; error: string };

export class ServerActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ServerActionError";
  }
}

export function getServerActionErrorMessage(
  error: string | null | undefined,
  fallback: string
) {
  if (!error) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(error) as {
      message?: string;
      error?: string;
    };

    return parsed.message || parsed.error || error;
  } catch {
    return error;
  }
}

export function createServerAction<Return, Args extends unknown[] = []>(
  callback: (...args: Args) => Promise<Return>
): (...args: Args) => Promise<ServerActionResult<Return>> {
  return async (...args: Args) => {
    try {
      const value = await callback(...args);
      return { success: true, value };
    } catch (error) {
      if (error instanceof ServerActionError)
        return { success: false, error: error.message };
      throw error;
    }
  };
}

export function withRBAC(config: RBACConfig) {
  return function <T extends (...args: any[]) => any>(action: T): T {
    return async function (
      ...args: Parameters<T>
    ): Promise<Awaited<ReturnType<T>>> {
      if (process.env.NODE_ENV !== "production") {
        return await action(...args);
      }

      try {
        var permissionsCookie = Cookies.get(PERMISSIONS_COOKIE_NAME);
        const permissions: IPathPermission[] =
          deserializePermissionsCookie(permissionsCookie);
        const hasPermission = permissions.some(
          (item: any) =>
            config.resourcePath.startsWith(item.path) &&
            item.permissions.some((s: any) =>
              config.requiredPermissions.includes(s)
            )
        );
        if (!hasPermission) {
          throw new PermissionDeniedError(
            "Bạn không đủ quyền để thực hiện tác vụ này"
          );
        }

        return await action(...args);
      } catch (error) {
        throw error;
      }
    } as T;
  };
}

export function serverActionMessage({
  response,
  successMessage,
  errorMessage,
}: {
  response: ServerActionResult<unknown>;
  successMessage: string;
  errorMessage: string;
}) {
  if (response.success) {
    toast.success(successMessage);
  } else {
    toast.error(getServerActionErrorMessage(response.error, errorMessage));
  }
}
